import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function TeacherDashboard({ user, onLogout }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`, { headers: getAuthHeaders() });
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="dashboard"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="dashboard">
      <div className="p-6 lg:p-8 space-y-6" data-testid="teacher-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-navy">Welcome, {user.first_name}!</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Manage your courses and students</p>
          </div>
          <Button
            onClick={() => navigate('/courses')}
            className="bg-primary-blue hover:bg-[#1874CD] text-white flex items-center space-x-2 rounded-xl transition-smooth btn-press"
            data-testid="go-to-courses-button"
          >
            <PlusCircle className="w-4 h-4" strokeWidth={2} />
            <span>Create Course</span>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#4D4D4D]">Total Courses</p>
                  <p className="text-3xl font-bold mt-2 text-primary-blue" data-testid="total-courses-count">{courses.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-blue/10">
                  <BookOpen className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#4D4D4D]">Total Students</p>
                  <p className="text-3xl font-bold mt-2 text-primary-blue" data-testid="total-students-count">
                    {courses.reduce((sum, course) => sum + (course.students_assigned?.length || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-blue/10">
                  <Users className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4 text-navy">Quick Start Guide</h3>
            <div className="space-y-3 text-[#4D4D4D]">
              <p className="flex items-start space-x-2">
                <span className="font-bold text-primary-blue">1.</span>
                <span>Go to "My Courses" and create a new course</span>
              </p>
              <p className="flex items-start space-x-2">
                <span className="font-bold text-primary-blue">2.</span>
                <span>Add modules to organize your course content</span>
              </p>
              <p className="flex items-start space-x-2">
                <span className="font-bold text-primary-blue">3.</span>
                <span>Add lessons with YouTube video links to each module</span>
              </p>
              <p className="flex items-start space-x-2">
                <span className="font-bold text-primary-blue">4.</span>
                <span>Create quizzes for each lesson (MCQ or True/False)</span>
              </p>
              <p className="flex items-start space-x-2">
                <span className="font-bold text-primary-blue">5.</span>
                <span>Assign students to your course</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
